import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { computeRecipe, totalDays } from "../lib/calc";
import type { IngredientLine, StepLine } from "../lib/types";

const KINDS: IngredientLine["kind"][] = ["spirit", "water", "sugar", "fermentable", "yeast", "other"];
const UNITS = ["ml", "L", "g", "kg", "each"];

function blankIngredient(sortOrder: number): IngredientLine {
  return { id: crypto.randomUUID(), name: "", amountValue: 0, amountUnit: "ml", kind: "other", sortOrder };
}
function blankStep(stepNumber: number): StepLine {
  return { id: crypto.randomUUID(), stepNumber, title: "", body: "", dayOffsetStart: 0, dayOffsetEnd: 0, whenLabel: "" };
}

export function RecipeBuilder() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [id, setId] = useState<string | undefined>(routeId);
  const [title, setTitle] = useState("Untitled recipe");
  const [blurb, setBlurb] = useState("");
  const [method, setMethod] = useState("Cold maceration");
  const [baseSpirit, setBaseSpirit] = useState("");
  const [ingredients, setIngredients] = useState<IngredientLine[]>([blankIngredient(0)]);
  const [steps, setSteps] = useState<StepLine[]>([blankStep(1)]);
  const [changelog, setChangelog] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!routeId);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!routeId) return;
    api.get<{ recipe: any; version: any }>(`/recipes/${routeId}`).then((res) => {
      setTitle(res.recipe.title);
      setBlurb(res.recipe.blurb);
      setMethod(res.recipe.method);
      setBaseSpirit(res.recipe.baseSpirit);
      setIngredients(res.version.ingredients.length ? res.version.ingredients : [blankIngredient(0)]);
      setSteps(res.version.steps.length ? res.version.steps : [blankStep(1)]);
      setLoaded(true);
    });
  }, [routeId]);

  const computed = useMemo(() => computeRecipe(ingredients), [ingredients]);
  const days = useMemo(() => totalDays(steps), [steps]);

  function scheduleSave(nextIngredients = ingredients, nextSteps = steps) {
    if (!id) return; // brand-new recipe: saved explicitly first
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.patch(`/recipes/${id}/draft`, { title, blurb, method, baseSpirit, ingredients: nextIngredients, steps: nextSteps });
      } finally {
        setSaving(false);
      }
    }, 700);
  }

  async function ensureCreated() {
    if (id) return id;
    setSaving(true);
    try {
      const res = await api.post<{ recipeId: string }>("/recipes", { title, blurb, method, baseSpirit, ingredients, steps });
      setId(res.recipeId);
      navigate(`/recipes/${res.recipeId}/edit`, { replace: true });
      return res.recipeId;
    } finally {
      setSaving(false);
    }
  }

  function updateIngredient(idx: number, patch: Partial<IngredientLine>) {
    const next = ingredients.map((ing, i) => (i === idx ? { ...ing, ...patch } : ing));
    setIngredients(next);
    scheduleSave(next, steps);
  }
  function addIngredient() {
    const next = [...ingredients, blankIngredient(ingredients.length)];
    setIngredients(next);
    scheduleSave(next, steps);
  }
  function removeIngredient(idx: number) {
    const next = ingredients.filter((_, i) => i !== idx);
    setIngredients(next);
    scheduleSave(next, steps);
  }

  function updateStep(idx: number, patch: Partial<StepLine>) {
    const next = steps.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setSteps(next);
    scheduleSave(ingredients, next);
  }
  function addStep() {
    const next = [...steps, blankStep(steps.length + 1)];
    setSteps(next);
    scheduleSave(ingredients, next);
  }
  function removeStep(idx: number) {
    const next = steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stepNumber: i + 1 }));
    setSteps(next);
    scheduleSave(ingredients, next);
  }

  async function saveNow() {
    const createdId = await ensureCreated();
    if (createdId && id) {
      await api.patch(`/recipes/${createdId}/draft`, { title, blurb, method, baseSpirit, ingredients, steps });
    }
  }

  async function publish() {
    await saveNow();
    const targetId = id ?? (await ensureCreated());
    await api.post(`/recipes/${targetId}/publish`, { changelog: changelog || undefined });
    navigate(`/recipes/${targetId}`);
  }

  if (!loaded) return <main className="page">Loading…</main>;

  return (
    <main style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 340px", alignItems: "start", minHeight: "calc(100vh - 160px)" }}>
      <section style={{ padding: "32px 40px 70px", borderRight: "2px solid var(--color-divider)", minWidth: 0 }}>
        <div className="flex-row" style={{ marginBottom: 8 }}>
          <span className="tag tag-accent">Draft · {saving ? "saving…" : "saved"}</span>
        </div>
        <input
          className="input"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            scheduleSave();
          }}
          style={{
            fontFamily: "var(--font-heading)",
            fontWeight: 800,
            fontSize: 34,
            letterSpacing: "-0.025em",
            minHeight: 58,
            padding: "6px 10px",
            background: "transparent",
            borderColor: "transparent",
            marginLeft: -11,
            width: "calc(100% + 11px)",
          }}
        />
        <textarea
          className="input"
          value={blurb}
          onChange={(e) => {
            setBlurb(e.target.value);
            scheduleSave();
          }}
          placeholder="A sentence or two about this recipe…"
          style={{ marginTop: 8, minHeight: 64, fontSize: 14.5, lineHeight: 1.6, background: "transparent", borderColor: "transparent", paddingLeft: 0 }}
        />

        <div className="flex-row" style={{ marginTop: 10, flexWrap: "wrap" }}>
          <div className="field" style={{ minWidth: 220 }}>
            <label>Method</label>
            <select
              className="input"
              value={method}
              onChange={(e) => {
                setMethod(e.target.value);
                scheduleSave();
              }}
            >
              {["Cold maceration", "Warm infusion", "Fat / oleo saccharum", "Blend & sweeten only", "Fermentation"].map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ minWidth: 220 }}>
            <label>{method === "Fermentation" ? "Style" : "Base spirit"}</label>
            <input
              className="input"
              value={baseSpirit}
              onChange={(e) => {
                setBaseSpirit(e.target.value);
                scheduleSave();
              }}
              placeholder={method === "Fermentation" ? "Dry mead, session cider, kombucha…" : "Neutral 96%, vodka, brandy…"}
            />
          </div>
        </div>

        <div className="spread" style={{ borderBottom: "2px solid var(--color-divider)", paddingBottom: 12, marginTop: 26 }}>
          <h2 style={{ margin: 0, fontSize: 20, letterSpacing: "-0.015em" }}>Ingredients</h2>
        </div>
        {ingredients.map((row, i) => (
          <div key={row.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--color-divider)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 130px 34px", gap: 10, marginBottom: 6 }}>
              <input className="input" placeholder="Ingredient name" value={row.name} onChange={(e) => updateIngredient(i, { name: e.target.value })} />
              <select className="input" value={row.kind} onChange={(e) => updateIngredient(i, { kind: e.target.value as IngredientLine["kind"] })}>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
              <button type="button" className="btn btn-icon" style={{ color: "color-mix(in srgb, var(--color-text) 45%, transparent)" }} onClick={() => removeIngredient(i)}>
                ✕
              </button>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  row.kind === "spirit" || row.kind === "fermentable" || row.kind === "yeast"
                    ? "100px 90px 90px minmax(0,1fr) minmax(0,1fr)"
                    : "100px 90px minmax(0,1fr) minmax(0,1fr)",
                gap: 10,
              }}
            >
              <input
                className="input tabular"
                type="number"
                value={row.amountValue}
                onChange={(e) => updateIngredient(i, { amountValue: Number(e.target.value) })}
              />
              <select className="input" value={row.amountUnit} onChange={(e) => updateIngredient(i, { amountUnit: e.target.value as IngredientLine["amountUnit"] })}>
                {UNITS.map((u) => (
                  <option key={u}>{u}</option>
                ))}
              </select>
              {row.kind === "spirit" && (
                <input
                  className="input tabular"
                  type="number"
                  placeholder="% ABV"
                  value={row.abvPercent ?? ""}
                  onChange={(e) => updateIngredient(i, { abvPercent: Number(e.target.value) })}
                />
              )}
              {row.kind === "fermentable" && (
                <input
                  className="input tabular"
                  type="number"
                  placeholder="% sugar"
                  title="What share of this is fermentable sugar (honey ~80%, apple juice ~11%)"
                  value={row.sugarContentPercent ?? ""}
                  onChange={(e) => updateIngredient(i, { sugarContentPercent: Number(e.target.value) })}
                />
              )}
              {row.kind === "yeast" && (
                <input
                  className="input tabular"
                  type="number"
                  placeholder="% attenuation"
                  title="Expected apparent attenuation, e.g. 75 for most wine/beer/mead yeast"
                  value={row.attenuationPercent ?? ""}
                  onChange={(e) => updateIngredient(i, { attenuationPercent: Number(e.target.value) })}
                />
              )}
              <input className="input" placeholder="Role (base, bittering…)" value={row.role ?? ""} onChange={(e) => updateIngredient(i, { role: e.target.value })} />
              <input className="input" placeholder="Note" value={row.note ?? ""} onChange={(e) => updateIngredient(i, { note: e.target.value })} />
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-secondary" style={{ marginTop: 14 }} onClick={addIngredient}>
          + Add ingredient
        </button>

        <div className="spread" style={{ borderBottom: "2px solid var(--color-divider)", paddingBottom: 12, marginTop: 38 }}>
          <h2 style={{ margin: 0, fontSize: 20, letterSpacing: "-0.015em" }}>Steps</h2>
          <span className="muted-strong" style={{ fontSize: 11.5 }}>
            "Day ends" set the schedule when you brew this
          </span>
        </div>
        {steps.map((s, i) => (
          <div key={s.id} style={{ display: "grid", gridTemplateColumns: "40px minmax(0,1fr) 90px 90px 34px", gap: 10, padding: "14px 0", borderBottom: "1px solid var(--color-divider)", alignItems: "start" }}>
            <span className="tabular" style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 13, paddingTop: 9 }}>
              {String(s.stepNumber).padStart(2, "0")}
            </span>
            <div>
              <input className="input" placeholder="Step title" value={s.title} onChange={(e) => updateStep(i, { title: e.target.value })} style={{ marginBottom: 6 }} />
              <textarea className="input" placeholder="What to do" value={s.body} onChange={(e) => updateStep(i, { body: e.target.value })} style={{ minHeight: 56, fontSize: 14, lineHeight: 1.55 }} />
            </div>
            <input
              className="input tabular"
              type="number"
              title="Day this step ends"
              value={s.dayOffsetEnd}
              onChange={(e) => updateStep(i, { dayOffsetEnd: Number(e.target.value) })}
            />
            <input className="input" placeholder="When label" value={s.whenLabel} onChange={(e) => updateStep(i, { whenLabel: e.target.value })} />
            <button type="button" className="btn btn-icon" style={{ color: "color-mix(in srgb, var(--color-text) 45%, transparent)" }} onClick={() => removeStep(i)}>
              ✕
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-secondary" style={{ marginTop: 14 }} onClick={addStep}>
          + Add step
        </button>
      </section>

      <aside style={{ padding: "32px 28px 60px", position: "sticky", top: 0, display: "flex", flexDirection: "column", gap: 26 }}>
        <div className="panel">
          <h6 className="eyebrow panel-title">Strength, live</h6>
          <p className="tabular" style={{ margin: 0, fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 56, lineHeight: 1, color: "var(--color-accent)" }}>
            {computed.abvPercent}%
          </p>
          <p className="muted" style={{ margin: "6px 0 0", fontSize: 12.5 }}>
            {(computed.abvPercent * 2).toFixed(1)} proof · {days} day{days === 1 ? "" : "s"} total
          </p>
          <hr className="hr" style={{ margin: "16px 0" }} />
          <div className="spread" style={{ fontSize: 13, padding: "5px 0", borderTop: "1px solid var(--color-divider)", marginTop: 6 }}>
            <span>Yield</span>
            <span className="tabular" style={{ fontFamily: "var(--font-heading)", fontWeight: 800 }}>
              {(computed.yieldMl / 1000).toFixed(2)} L
            </span>
          </div>
          <div className="spread" style={{ fontSize: 13, padding: "5px 0" }}>
            <span>Sugar</span>
            <span className="tabular">{computed.sugarGPerL} g/L</span>
          </div>
          {computed.originalGravity != null && (
            <div className="spread" style={{ fontSize: 13, padding: "5px 0" }}>
              <span>Gravity, est.</span>
              <span className="tabular">
                {computed.originalGravity.toFixed(3)} → {computed.finalGravity?.toFixed(3)}
              </span>
            </div>
          )}
        </div>

        <div className="field">
          <label>Changelog for this version</label>
          <input className="input" placeholder="What changed?" value={changelog} onChange={(e) => setChangelog(e.target.value)} />
        </div>

        <div className="flex-col" style={{ gap: 8, borderTop: "2px solid var(--color-divider)", paddingTop: 18 }}>
          <button type="button" className="btn btn-primary btn-block" onClick={publish}>
            Publish
          </button>
          <button type="button" className="btn btn-secondary btn-block" onClick={saveNow}>
            Save draft without publishing
          </button>
        </div>
      </aside>
    </main>
  );
}
